import { Modal } from 'antd';
import { t } from 'helpers/i18n';
import React, { useCallback, useRef, useState } from 'react';

type NotificationPermissionStatus = NotificationPermission | 'unsupported';

const isNotificationSupported = () =>
  typeof window !== 'undefined' && 'Notification' in window;

const getBrowserPermission = (): NotificationPermissionStatus =>
  isNotificationSupported() ? Notification.permission : 'unsupported';

const useNotificationPermission = () => {
  // The explanatory modal is shown at most once per app session.
  const hasAskedRef = useRef(false);

  const [permissionStatus, setPermissionStatus] = useState(
    getBrowserPermission
  );
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMessage, setModalMessage] = useState<React.ReactNode>(null);

  const requestPermission = useCallback((message: React.ReactNode) => {
    if (!isNotificationSupported()) return;

    const browserPermission = getBrowserPermission();
    // Keep the reactive status in sync (e.g. changed from the browser UI).
    setPermissionStatus(browserPermission);
    if (browserPermission !== 'default') return;

    // permission === 'default': ask once.
    if (hasAskedRef.current) return;
    hasAskedRef.current = true;
    setModalMessage(message);
    setModalVisible(true);
  }, []);

  const handleOk = useCallback(() => {
    setModalVisible(false);
    if (!isNotificationSupported()) return;

    Notification.requestPermission()
      .then(permission => setPermissionStatus(permission))
      .catch(() => undefined);
  }, []);

  const handleCancel = useCallback(() => {
    setModalVisible(false);
  }, []);

  const permissionModal = (
    <Modal
      visible={modalVisible}
      title={t('BrowserPermissions.Title')}
      onOk={handleOk}
      onCancel={handleCancel}
    >
      {modalMessage}
    </Modal>
  );

  return { permissionStatus, requestPermission, permissionModal };
};

export default { useNotificationPermission };
